import React, { useEffect, useState } from 'react';
import Loader from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import HorizontalDotsButton from '@/components/HorizontalDotsButton/HorizontalDotsButton';
import {
	createSequenceTemplate,
	deleteSequenceTemplate,
	updateSequenceTemplate
} from '@/services/sequencesService';
import { toast } from 'react-hot-toast';
import Dialog from '@/components/Dialog/Dialog';
import Button from '@/components/Button/Button';
import { Checkmark, CloseThin } from '@/resources/assets/images';
import { useTemplates, type TemplatesType } from '../../hooks/useTemplates';
import { useSequencesContext } from '../../SequencesContext';

export default function NewSequenceContainer() {
	return (
		<div className="flex h-full flex-col px-3">
			<h2 className="whitespace-nowrap text-3xl font-medium text-gray-200">
				New sequence creation
			</h2>
			<StartFromScratchButton />
			<TemplateSelectList />
		</div>
	);
}

function StartFromScratchButton() {
	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const {
		setSequencePage,
		setSelectedTemplate,
		setSelectedStep,
		selectedSequence,
		setSelectedSequence
	} = useSequencesContext();

	function handleGoToStepsCreation() {
		if (!selectedSequence) return;
		const createdStep = {
			id: Math.random().toString(36).substring(2, 9),
			subject: 'New Email',
			body: 'This is the new template',
			sendAfterInterval: 0,
			sendAfterType: 'DAYS',
			order: 0,
			justCreated: true
		};
		setSelectedTemplate({
			id: Math.random().toString(36).substring(2, 9),
			name: 'New Template',
			steps: [createdStep]
		});

		setSelectedStep(createdStep);

		setSelectedSequence({
			...selectedSequence,
			steps: [createdStep]
		});

		setSequencePage('STEPS-CREATION');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'user_clicked_start_sequence_from_scratch'
		});
	}

	return (
		<button
			onClick={handleGoToStepsCreation}
			className="group my-3 flex h-24 w-full items-center gap-2 rounded-md border border-dark-grey bg-dm-black pl-8 hover:border-transparent hover:bg-orange"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth={1.5}
				stroke="currentColor"
				className="h-6 w-6 text-orange group-hover:text-black"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="M12 4.5v15m7.5-7.5h-15"
				/>
			</svg>
			<span className="text-xl text-gray-200 group-hover:text-black">
				Start from scratch
			</span>
		</button>
	);
}

function TemplateSelectList() {
	const { templates, getTemplates, isLoading } = useTemplates();

	useEffect(() => {
		getTemplates();
	}, []);

	return (
		<div className="h-5/6 overflow-y-auto rounded-lg border border-dark-grey bg-dm-black">
			<div className="flex h-24 items-center border-b border-dark-grey">
				<span className="ml-8 text-xl font-medium text-gray-200">
					Select a template
				</span>
			</div>
			{isLoading ? (
				<div className="mt-36 flex items-center justify-center">
					<Loader />
				</div>
			) : (
				<>
					{templates?.map(template => (
						<TemplateSelectListItem
							key={template.id}
							template={template}
						/>
					))}
				</>
			)}
		</div>
	);
}

function TemplateSelectListItem({ template }: { template: TemplatesType }) {
	const {
		selectedTemplate,
		setSelectedTemplate,
		setSelectedStep,
		selectedSequence,
		setSelectedSequence
	} = useSequencesContext();

	const { getTemplates } = useTemplates();

	const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [isEditingDescription, setIsEditingDescription] = useState(false);
	const [isEditingName, setIsEditingName] = useState(false);
	const [templateName, setTemplateName] = useState(template.name);
	const [description, setDescription] = useState(template.description);

	function handleSelect() {
		if (!selectedSequence) return;
		setSelectedTemplate(template);
		setSelectedStep(template.steps[0]);
		setSelectedSequence({
			...selectedSequence,
			steps: template.steps
		});
	}

	function handleDuplicateTemplate() {
		createSequenceTemplate({
			name: template.name,
			steps: template.steps,
			description: template.description
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error duplicating template! Please try again later.'
					);
				}
				toast.success('Template duplicated successfully!');
				getTemplates();
			})
			.catch(err => {
				toast.error(err.message);
			});
	}

	function handleDeleteTemplate() {
		if (!template.id) return;
		setIsDeleteLoading(true);
		deleteSequenceTemplate({ templateId: template.id })
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error deleting template! Please try again later.'
					);
				}
				toast.success('Template deleted successfully!');
				getTemplates();
			})
			.catch(err => {
				toast.error(err.message);
			})
			.finally(() => setIsDeletingTemplate(false));
	}

	function handleTemplateForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		handleUpdateTemplate();
	}

	function handleUpdateTemplate() {
		if (!template.id) return;
		updateSequenceTemplate({
			templateId: template.id,
			name: templateName || template.name || 'New template',
			description: description || ''
		})
			.then(res => {
				setIsEditingDescription(false);
				setIsEditingName(false);

				if (res.status !== 200) {
					throw new Error(
						'Error updating template! Please try again later.'
					);
				}

				toast.success('Template updated successfully!');
				getTemplates();
			})
			.catch(err => {
				toast.error(err.message);
			});
	}

	function handleDeleteOpen() {
		setIsDeletingTemplate(!isDeletingTemplate);
	}

	function handleCancelEdit() {
		setIsEditingDescription(false);
		setDescription(template.description);

		setIsEditingName(false);
		setTemplateName(template.name);
	}

	const dropdownTemplateOptions = [
		{ id: '1', label: 'Duplicate', onClick: handleDuplicateTemplate },
		...(!template.default
			? [
					{
						id: '2',
						label: 'Edit name',
						onClick: () => setIsEditingName(true)
					},
					{
						id: '3',
						label: 'Edit description',
						onClick: () => setIsEditingDescription(true)
					},
					{
						id: '6',
						label: 'Remove',
						onClick: () => setIsDeletingTemplate(true)
					}
				]
			: [])
	];

	const isSelected = selectedTemplate?.id === template.id;
	const [isHovered, setIsHovered] = useState<boolean>(false);

	return (
		<>
			<Dialog isOpen={isDeletingTemplate} handleOpen={handleDeleteOpen}>
				<span className="mb-2 text-2xl font-medium text-orange">
					Confirm delete
				</span>
				<span className="text-lg text-gray-200">
					Are you sure you want to remove the selected template?
				</span>
				<div className="flex justify-between">
					<div className="w-1/3">
						<Button variant="ghost" onClick={handleDeleteOpen}>
							Cancel
						</Button>
					</div>
					<div className="w-2/5">
						<Button variant="danger" onClick={handleDeleteTemplate}>
							{isDeleteLoading ? 'Deleting...' : 'Confirm Delete'}
						</Button>
					</div>
				</div>
			</Dialog>
			<div
				className={`flex h-24 cursor-pointer border-b py-4 pl-8 pr-4 hover:bg-dm-black3 ${isSelected ? 'border-orange bg-dm-black3' : 'border-dark-grey bg-dm-black'} `}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onClick={handleSelect}
			>
				<div className="flex w-2/5 items-center border-r border-dashed border-dark-grey">
					<div className="flex flex-col">
						{isEditingName ? (
							<form
								onSubmit={handleTemplateForm}
								className="flex h-full items-center gap-1"
							>
								<input
									value={templateName}
									onChange={e =>
										setTemplateName(e.target.value)
									}
									style={{
										width: '85%'
									}}
									className="border bg-gray-200 text-black"
									name="edit-name"
									id="edit-name"
								/>
								<div className="flex flex-col items-center gap-3 text-white">
									<button id="campaigns-rename-checkmark">
										<Checkmark width={17} />
									</button>
									<button
										onClick={handleCancelEdit}
										type="button"
									>
										<CloseThin width={12} />
									</button>
								</div>
							</form>
						) : (
							<p className="text-lg text-gray-200">
								{template.name}
							</p>
						)}

						<p>{template.steps.length} Steps</p>
					</div>
				</div>
				<div className="w-3/5 overflow-y-scroll">
					{isEditingDescription ? (
						<form onSubmit={handleTemplateForm} className="h-full">
							<textarea
								value={description}
								onChange={e => setDescription(e.target.value)}
								style={{
									width: '90%'
								}}
								className="ml-4 h-full border bg-gray-200 px-1 py-0 text-xs text-black"
								name="edit-description"
								id="edit-description"
							/>
						</form>
					) : (
						<p className="px-4 text-xs">{template.description}</p>
					)}
				</div>
				<div
					className={`${
						isSelected || isHovered ? 'block' : 'invisible'
					} rounded-lg`}
				>
					{isEditingDescription ? (
						<div className="flex h-full flex-col items-center justify-center gap-3 text-white">
							<button
								id="campaigns-rename-checkmark"
								onClick={handleUpdateTemplate}
							>
								<Checkmark width={20} />
							</button>
							<button onClick={handleCancelEdit} type="button">
								<CloseThin width={14} />
							</button>
						</div>
					) : (
						<DropdownMenu
							options={dropdownTemplateOptions}
							itemClassName="text-white py-3 px-2 hover:bg-dm-black3"
						>
							<HorizontalDotsButton />
						</DropdownMenu>
					)}
				</div>
			</div>
		</>
	);
}
